import styled, { useTheme } from "styled-components";
import { FC, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import Text from "@/components/Text";
import { hexToRgba } from "@/utils/theme";
import ToggableHeading from "@/components/ToggableHeading";
import { HighlightResource, ProductionScriptResource } from "@/types/resources";
import { api } from "@/utils/api";
import { Table, TableContent, Td, Th } from "@/components/Table";
import ScrollableTable from "@/components/ScrollableTable";

const Score = styled(Text)<{ $error: boolean }>`
  background: ${({ theme, $error }) =>
    $error ? theme.colors.errorLight : theme.colors.grayscaleLight};
  border-radius: 4px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HighlightCard = styled.div<{ $color: string }>`
  background: rgba(226, 159, 13, 0.1);
  border-radius: 4px;
  height: 32px;
  padding: 0 12px;
  background-color: ${(props) => hexToRgba(props.$color, 0.1)};
  transition: opacity ease-out 0.2s;
  border: solid 1px transparent;
  transition: border-color ease-out 0.2s;
  display: inline-flex;
  flex: 1;
  align-items: center;
  &:last-child {
    margin-bottom: 0;
  }
  ${Text} {
    color: ${(props) => props.$color};
  }
`;

const TotalScore = styled(Text)`
  justify-content: center;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  border-radius: 4px;
  height: 32px;
  display: flex;
  align-items: center;
`;

const TotalTitle = styled(Text)`
  height: 48px;
  display: flex;
  align-items: center;
`;

const StyledTable = styled(Table)<{ $width: string }>`
  box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.08);
  border-collapse: separate;
  width: ${({ $width }) => $width};
  th,
  td {
    border: 0.5px solid ${({ theme }) => theme.colors.grayscaleLight};
    width: 184px;
    &:first-child {
      padding-left: 24px;
      width: 275px;
      border-left: 0;
      position: sticky;
      left: 0;
    }
    &:nth-child(2) {
      width: 62px;
      text-align: center;
      position: sticky;
      left: 275px;
      border-right-width: 1px;
    }
    &:nth-child(3) {
      border-left: 0;
    }
    &:last-child {
      padding-right: 24px;
      border-right: 0;
    }
  }
  tr {
    &:first-child {
      th {
        border-top: 0;
      }
    }
    &:last-child {
      td {
        border-bottom: 0;
      }
    }
  }
  th {
    vertical-align: top;
    padding: 16px;
    white-space: initial;
  }

  td {
    padding: 8px 16px;
  }
`;

const LastRow = styled.tr`
  > td {
    border: 0.5px solid ${({ theme }) => theme.colors.grayscaleLight} !important;
    border-bottom: 0 !important;
    &:first-child {
      border-left: 0 !important;
    }
    &:last-child {
      border-right: 0 !important;
    }
  }
`;

const SceneTitle = styled.div`
  display: flex;
  align-items: flex-start;
`;

const Badge = styled(Text)`
  margin-right: 8px;
  width: 16px;
  height: 16px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.lightGray};
  display: inline-flex;
  flex-shrink: 0;
`;

const HighlightTitle = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const MandatoryIcon = styled.div`
  width: 16px;
  margin-left: 12px;
  line-height: 0;
`;

interface Props {
  activeScript: ProductionScriptResource;
}

const Highlights: FC<Props> = ({ activeScript }) => {
  const [isOpen, setIsOpen] = useState(true);

  const { teamId } = useParams();

  const theme = useTheme();

  const {
    data: teamHighlights,
    isLoading,
    isError,
  } = useQuery(["script_highlights", { teamId }], () =>
    api.get<never, HighlightResource[]>("/v1/production/highlights", {
      params: {
        "filter[team_id]": teamId,
      },
    })
  );

  const highlightScores = useMemo(() => {
    return teamHighlights?.reduce<{
      [highlightId: number]: {
        [sceneId: number]: number;
      };
    }>((acc, curr) => {
      acc[curr.id] = activeScript.scenes!.reduce<{ [sceneId: number]: number }>(
        (acc, scene) => {
          acc[scene.id] = scene.highlights!.reduce((sum, h) => {
            if (h.id !== curr.id) return sum;

            return sum + h.value;
          }, 0);
          return acc;
        },
        {}
      );
      return acc;
    }, {});
  }, [activeScript.scenes, teamHighlights]);

  const totals = useMemo(() => {
    return activeScript.scenes!.reduce<{ [sceneId: number]: number }>(
      (acc, scene) => {
        acc[scene.id] = scene.highlights!.reduce((sum, h) => {
          return sum + h.value;
        }, 0);
        return acc;
      },
      {}
    );
  }, [activeScript]);

  return (
    <>
      <ToggableHeading
        heading="HIGHLIGHTS"
        onToggle={() => setIsOpen((prev) => !prev)}
        isOpen={isOpen}
      />
      {isOpen && (
        <ScrollableTable>
          <StyledTable
            $fixed
            $width={activeScript.scenes!.length < 3 ? "auto" : "100%"}
          >
            <thead>
              <tr>
                <Th>Highlight Category</Th>
                <Th>Value</Th>
                {activeScript.scenes!.map((scene, index) => (
                  <Th key={scene.id}>
                    <SceneTitle>
                      <Badge $type="extraSmall">{index + 1}</Badge>
                      {scene.name}
                    </SceneTitle>
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody>
              <TableContent
                isLoading={isLoading}
                isError={isError}
                hasData={!!teamHighlights?.length}
              >
                {teamHighlights?.map((highlight) => (
                  <tr key={highlight.id}>
                    <Td>
                      <HighlightTitle>
                        <HighlightCard $color={highlight.color}>
                          <Text $type="smallBold">{highlight.title}</Text>
                        </HighlightCard>
                        <MandatoryIcon>
                          {highlight.isMandatory && (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 16 16"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle
                                cx="8"
                                cy="8"
                                r="8"
                                fill={theme.colors.error}
                              />
                              <path
                                d="M7.25 10.2647V11.5H8.75V10.2647H7.25ZM7.25 4.5V9.44118H8.75V4.5H7.25Z"
                                fill={theme.colors.white}
                              />
                            </svg>
                          )}
                        </MandatoryIcon>
                      </HighlightTitle>
                    </Td>
                    <Td>
                      <Text $type="smallBold">{highlight.value}</Text>
                    </Td>
                    {activeScript.scenes!.map((scene) => (
                      <Td key={scene.id}>
                        <Score
                          $type="smallBold"
                          $error={
                            highlight.isMandatory &&
                            !highlightScores![highlight.id][scene.id]
                          }
                        >
                          {highlightScores![highlight.id][scene.id] ?? 0}
                        </Score>
                      </Td>
                    ))}
                  </tr>
                ))}
                <LastRow>
                  <Td>
                    <TotalTitle $type="button" $color="black">
                      Total scenes points:
                    </TotalTitle>
                  </Td>
                  <Td />
                  {activeScript.scenes!.map((scene) => (
                    <Td key={scene.id}>
                      <TotalScore
                        $type="button"
                        $color={!!totals[scene.id] ? "black" : "gray"}
                      >
                        {totals[scene.id] ?? 0}
                      </TotalScore>
                    </Td>
                  ))}
                </LastRow>
              </TableContent>
            </tbody>
          </StyledTable>
        </ScrollableTable>
      )}
    </>
  );
};

export default Highlights;
